<template>
  <div class="available-products">
    <div class="navbar-background">
      <div class="available-products-nav-bar" data-cy="available-products-nav-bar">
        <h3 class="page-title" data-cy="availability-title">
          {{ $t(`products_availability.title`) }}
        </h3>
        <BreadcrumbMenu data-cy="breadcrumb-menu" :crumbs="breadcrumbItems" />
        <div class="action-buttons" data-cy="action-buttons">
          <BackstageButton
            :disabled="!currentStepActionIsEnabled"
            :icon-right="orderManagement.saving.value ? Icons.LOADING : Icons.ARROW_RIGHT"
            :data-cy="`${currentStep}-button-cnt`"
            :data-cy-button="`${currentStep}-button`"
            @click.prevent="handleStepAction">
            {{ currentStepActionButtonLabel }}
          </BackstageButton>
          <BackstageButton
            :variant="ButtonVariant.SECONDARY"
            data-cy="cancel-button-cnt"
            data-cy-button="cancel-button"
            v-on:click="exit()">
            {{ $t('global.cancel_button') }}
          </BackstageButton>
        </div>
      </div>
    </div>
    <div class="current-step-content">
      <ProductsModelsSelectorV2
        v-if="currentStepState.isModelSelection"
        :order-type="OrderType.ReOrder"
        :order-date="formatDateToIsoStringIgnoringTime(getCurrentDate())"
        hide-select-button />

      <ProductsQuantitiesSelectorV3
        v-if="currentStepState.isQuantities"
        :seasonStart="orderManagement.seasonStartDate.value" />

      <ProductsConfirmationModal
        v-if="orderManagement.showProductsConfirmationModal.value"
        @proceed="handleDraftCreation(true)">
        <BackstageButton
          @click="orderManagement.showProductsConfirmationModal.value = false"
          :variant="ButtonVariant.SECONDARY">
          <BackstageIcon name="arrow-left" />
          <span class="spacer"> {{ $t(`viewOrder.confirmation_modal.go_back`) }}</span>
        </BackstageButton>
        <BackstageButton @click="handleDraftCreation(false)" :variant="ButtonVariant.PRIMARY">
          <span>{{ $t(`viewOrder.confirmation_modal.proceed`) }}</span>
        </BackstageButton>
      </ProductsConfirmationModal>
    </div>
    <div v-if="showOrderCreatePopUp">
      <NotificationMessage
        :title="$t('createOrdersPopup.select_contact')"
        :size="'medium'"
        v-on:close="showOrderCreatePopUp = false">
        <InitNewOrder
          :isOrderFromAvailability="true"
          v-on:create-order="handleSalesRepDraftCreation"
          v-on:close="showOrderCreatePopUp = false" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BreadcrumbMenu from '@/components/common/breadcrumb-menu.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import ProductsConfirmationModal from '@/components/matrix/products-confirmation-modalV3.vue';
import ProductsModelsSelectorV2 from '@/components/matrix/products-models-selector-v2.vue';
import ProductsQuantitiesSelectorV3 from '@/components/matrix/products-quantities-selector-v3.vue';
import InitNewOrder from '@/components/orders/init-new-order.vue';
import { useAnalytics } from '@/composables/analytics';
import { useBPLogger } from '@/composables/business-process-logger';
import { useBreadcrumbMenu } from '@/composables/useBreadcrumbMenu';
import { useNewRelic } from '@/composables/useNewRelic';
import { useNotification } from '@/composables/useNotification';
import { useOrderManagement } from '@/composables/useOrderManagement';
import { useOrderValidation } from '@/composables/useOrderValidation';
import { UserRole } from '@/constants/user-role.constants';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { AvailabilityBreadcrumbSteps } from '@/models/available-products-breadcrumb-steps.enum';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { RouteNames } from '@/router';
import { useOrderStore } from '@/stores/order';
import { useProductMatrixStore } from '@/stores/product-matrix';
import { OrderType } from '@onrunning/backstage-models';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import BackstageButton from '../components/common/BackstageButton.vue';
import { useFormat } from '../composables/format';
import { useUserStore } from '../stores/user';

const showOrderCreatePopUp = ref(false);
const isRedirectingToOrderCreation = ref(false);
const contactId = ref<string | undefined>(undefined);

const { t } = useI18n();
const notification = useNotification();
const { formatDateToIsoStringIgnoringTime } = useFormat();
const { pageViewBPLog } = useBPLogger();
const { addCustomGaEvent } = useAnalytics();
const newRelicAgent = useNewRelic();
const orderManagement = useOrderManagement({ contactId });
const orderValidation = useOrderValidation();
const router = useRouter();
const route = useRoute();

const productMatrixStore = useProductMatrixStore();
const userStore = useUserStore();
const orderStore = useOrderStore();

productMatrixStore.initMatrixOrder(new Date());
orderStore.setOrderTypeInfo(
  {
    type: OrderType.ReOrder,
    startDate: formatDateToIsoStringIgnoringTime(new Date()),
    defaultDate: formatDateToIsoStringIgnoringTime(new Date()),
  },
  OrderType.ReOrder,
);

const currentStepState = computed(() => {
  return {
    isModelSelection: currentStep.value === AvailabilityBreadcrumbSteps.productsModelsSelection,
    isQuantities: currentStep.value === AvailabilityBreadcrumbSteps.productsQuantities,
  };
});

const currentStepActionIsEnabled = computed(() => {
  switch (currentStep.value) {
    case AvailabilityBreadcrumbSteps.productsModelsSelection:
      return productMatrixStore.selectedProductModelsNames.size > 0;
    case AvailabilityBreadcrumbSteps.productsQuantities:
      return orderValidation.matrixStats.value.allQuantitiesValid;
    default:
      return true;
  }
});

const currentStepActionButtonLabel = computed((): string => {
  if (currentStep.value === AvailabilityBreadcrumbSteps.productsModelsSelection) {
    return t(`products_availability.execute_${currentStep.value}_step_button`);
  } else {
    return t('viewHome.place_new_order_button');
  }
});

const handleStepAction = () => {
  switch (currentStep.value) {
    case AvailabilityBreadcrumbSteps.productsModelsSelection:
      navigateToQuantities();
      break;
    case AvailabilityBreadcrumbSteps.productsQuantities:
      handlePlaceNewOrderClick();
      break;
    default:
      break;
  }
};

const breadCrumbConfig = computed(() => {
  return {
    steps: Object.values(AvailabilityBreadcrumbSteps),
    translationNamespace: 'create_edit_order',
    routePrefix: '/products-availability/',
  };
});

const { breadcrumbItems, currentStep } = useBreadcrumbMenu<AvailabilityBreadcrumbSteps>(breadCrumbConfig);

onMounted(() => {
  pageViewBPLog(true, { page_name: 'products-availability' });
});

onBeforeUnmount(() => {
  // Only reset stores if we're not redirecting to order creation
  if (!isRedirectingToOrderCreation.value) {
    productMatrixStore.reset();
    orderStore.reset();
  }
});

function exit() {
  router.push('/products');
}

function navigateToQuantities() {
  return router.replace({
    params: { ...route.params, step: AvailabilityBreadcrumbSteps.productsQuantities },
  });
}

function getCurrentDate(): Date {
  return new Date();
}

function handlePlaceNewOrderClick() {
  // If the user is a sales rep, show the order creation popup for selecting a contact
  if (userStore.isASalesRep) {
    showOrderCreatePopUp.value = true;

    return;
  }

  orderManagement.showProductsConfirmationModal.value = true;
}

function handleSalesRepDraftCreation(params: { contactId: string }) {
  contactId.value = params.contactId;

  orderManagement.showProductsConfirmationModal.value = true;
}

async function handleDraftCreation(autoProceed: boolean = false) {
  try {
    // Set flag to prevent store reset on unmount since we're redirecting to order creation
    isRedirectingToOrderCreation.value = true;

    addCustomGaEvent({
      event: GaCustomEvents.OrderCreationFromAvailabilityPage,
      user_type: userStore.isASalesRep ? UserRole.SalesRep : UserRole.Dealer,
      order_type: OrderType.ReOrder,
    });

    await orderManagement.onProceedToOrderDetails(autoProceed);

    const routerName = userStore.isASalesRep ? RouteNames.CreateOrderForSalesRep : RouteNames.CreateOrderForDealer;

    router.push({
      name: routerName,
      params: {
        orderType: OrderType.ReOrder.toLocaleLowerCase(),
        startDate: formatDateToIsoStringIgnoringTime(getCurrentDate()),
        step: AddEditOrderBreadcrumbSteps.orderDetails,
        contactId: contactId.value,
      },
    });
  } catch (error: any) {
    isRedirectingToOrderCreation.value = false;
    notification.notifyError(t('global.error'));
    newRelicAgent.noticeError(error.message ?? 'An error occurred while creating the order');
  }
}
</script>

<style lang="scss" scoped>
.available-products {
  margin-bottom: 20px;

  .navbar-background {
    @include navbar-background;
  }

  .available-products-nav-bar {
    @include common-container;

    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    @include breakpoint(medium) {
      padding-bottom: 10px;
    }

    .page-title {
      font-size: 20px;
      font-weight: 400;
      text-align: left;
    }

    .action-buttons {
      min-width: 160px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;

      a {
        margin-left: auto;
      }

      :deep() .hidden {
        display: none;
      }
    }
  }
}
</style>
